import { FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Row } from 'reactstrap';
import { TEAM_PERFORMANCE_DASHBOARD } from '../../../consts/consts';
import { CampaignWithConfigs } from '../../../types';
import { INPUT_TYPES } from '../../Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from '../../Widgets/Modals/ModalEditor';
import { useConfiguration } from './configuration';

type Props = {
  isOpen: boolean;
  onToggle: () => void;
  selectedValues: any[];
  inputFields: any[];
  campaign: CampaignWithConfigs;
  onSuccess: (status: ApiStatus) => void;
};

export type ApiStatus = 'modified' | 'unchanged';

type ApiResponse = {
  status: ApiStatus;
  dataset_id: number;
};

type CheckboxValueType = 'Y' | 'N' | '-';

const CampaignParticipantsBulkEditorModal: FC<Props> = ({
  isOpen,
  onToggle,
  selectedValues,
  campaign,
  inputFields,
  onSuccess,
}) => {
  const { formatMessage } = useIntl();
  const campaignSurveyResponseParticipationOptions = useConfiguration(campaign);

  const handleCallback = useCallback(
    (data?: ApiResponse, error?: unknown, hardErrorMessage?: unknown) => {
      if (!error && !hardErrorMessage && data) {
        onSuccess(data.status);
      }
    },
    [onSuccess]
  );

  const hasManagers = useMemo(() => {
    return selectedValues.some(
      (item) =>
        item.additional_direct_reports_count !== 0 ||
        item.direct_reports_count !== 0
    );
  }, [selectedValues]);

  const filteredInputFields = useMemo(() => {
    return inputFields.map((item) => {
      if (
        item.name === 'is_participating_in_evaluation_phase' &&
        !hasManagers
      ) {
        return {
          ...item,
          hidden: true,
        };
      }
      return item;
    });
  }, [inputFields, hasManagers]);

  const fields = filteredInputFields.map((item) => {
    const { name, label, helperText, hidden } = item;
    return {
      name,
      label,
      helperText,
      type: hidden ? INPUT_TYPES.HIDDEN : INPUT_TYPES.CHECKBOX,
      withIndeterminate: true,
      checkedValue: 'Y',
      uncheckedValue: 'N',
      indeterminateValue: '-',
      hideLabel: !!hidden,
      formGroupClassName: hidden ? 'mb-0' : undefined,
      noFormGroupMargin: !!hidden,
    };
  });

  const form: { [name: string]: CheckboxValueType } = fields.reduce(
    (acc, item) => {
      const values: boolean[] = selectedValues.map(
        (person) => person[item.name]
      );
      const hasTrue = values.includes(true);
      const hasFalse = values.includes(false);
      return {
        ...acc,
        [item.name]: hasTrue && hasFalse ? '-' : hasTrue ? 'Y' : 'N',
      };
    },
    {}
  );

  const renderInputs = useCallback((inputs: JSX.Element[]) => {
    return (
      <>
        <Row className="mb-3 px-3">
          <FormattedMessage
            id="app.views.administration.campaign_participants.campaign_participants_bulk_editor_modal.intro"
            defaultMessage="Note: if someone will be on vacation during the cycle and needs to complete a section earlier or later, they can still complete it using a secret link (available in the FAQs tab) — once the cycle is published."
          />
        </Row>
        <Row>{inputs}</Row>
      </>
    );
  }, []);

  const transformObjectBeforeSubmit = useCallback(
    (payload) => {
      const transformedPayload =
        campaignSurveyResponseParticipationOptions.reduce((acc, item) => {
          if (item.transformBeforeSubmit) {
            acc[item.field] = item.transformBeforeSubmit(
              payload[item.field],
              payload,
              selectedValues
            );
          }
          return acc;
        }, {});

      return {
        ...payload,
        ...transformedPayload,
        selected_values: selectedValues
          .filter((item) => !!item.email)
          .map((item) => item.email),
      };
    },
    [campaignSurveyResponseParticipationOptions, selectedValues]
  );

  return (
    <>
      <ModalEditor
        title={formatMessage(
          {
            id: 'app.views.administration.campaign_participants_bulk_editor_modal.title',
            defaultMessage:
              'Edit {count, plural, one {# participant} other {# participants}} for {campaignName}',
          },
          { count: selectedValues.length, campaignName: campaign.name }
        )}
        callback={handleCallback}
        isOpen={isOpen}
        toggle={onToggle}
        method="POST"
        url={`/datasets/${campaign.dataset_id}/bulk-update`}
        submitText={formatMessage({
          id: 'app.views.administration.campaign_participants_bulk_editor_modal.button.save',
          defaultMessage: 'Save',
        })}
        object={form}
        inputs={fields}
        transformObjectBeforeSubmit={transformObjectBeforeSubmit}
        footer={
          <div className="col pb-4 text-center">
            <FormattedMessage
              id="app.views.administration.campaign_participants_bulk_editor_modal.footer"
              defaultMessage={
                "Want to edit a person's attributes for this cycle dataset snapshot? <br></br> Go to the <link>Team Dashboard</link>"
              }
              values={{
                link: (chunk) => (
                  <a
                    href={TEAM_PERFORMANCE_DASHBOARD().path}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {chunk}
                  </a>
                ),
                br: () => <br />,
              }}
            />
          </div>
        }
        renderInputs={renderInputs}
      />
    </>
  );
};

export default CampaignParticipantsBulkEditorModal;
